"use client";

import { SocialIcon } from "@webdo/community-ui";
import { useEffect, useId, useRef, useState } from "react";
import { usePortalTranslations } from "../i18n/client";
import { communityLoginHref, communityRequest, type Comments, type ReactionSummary } from "../lib/community";
import { CommunityComments } from "./CommunityComments";
import { CommunityReactions } from "./CommunityReactions";
import styles from "./Community.module.css";

export function CommunityEngagement({ targetType, targetUuid, authenticated, initialReactions, initialCommentCount, expanded = false }: {
  targetType: "post" | "discussion"; targetUuid: string; authenticated: boolean; initialReactions?: ReactionSummary[]; initialCommentCount?: number; expanded?: boolean;
}) {
  const t = usePortalTranslations();
  const [open, setOpen] = useState(expanded);
  const [opened, setOpened] = useState(expanded);
  const [commentCount, setCommentCount] = useState<number | null>(initialCommentCount ?? null);
  const element = useRef<HTMLDivElement>(null);
  const id = useId();
  const loginHref = communityLoginHref(targetType === "discussion" ? `/discussions/${targetUuid}` : "/");

  useEffect(() => {
    if (initialCommentCount !== undefined || opened || !element.current) return;
    const controller = new AbortController();
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      communityRequest<Comments>(`${targetType}/${targetUuid}/comments?offset=0&limit=1`, { signal: controller.signal }).then((result) => { if (!controller.signal.aborted) setCommentCount(result.total); }).catch(() => { /* The expanded thread provides the retry action. */ });
    }, { rootMargin: "100px" });
    observer.observe(element.current);
    return () => { observer.disconnect(); controller.abort(); };
  }, [initialCommentCount, opened, targetType, targetUuid]);

  return <div ref={element} className={styles.engagement}>
    <div className={styles.engagementBar}>
      <CommunityReactions key={JSON.stringify(initialReactions)} targetType={targetType} targetUuid={targetUuid} authenticated={authenticated} loginHref={loginHref} initialItems={initialReactions} />
      <button type="button" className={styles.iconAction} aria-label={t(open ? "community.closeComments" : "community.comments")} aria-expanded={open} aria-controls={id} onClick={() => { setOpen(!open); setOpened(true); }}><SocialIcon name="comment" /><span aria-label={commentCount === null ? t("community.loading") : t("community.commentCount", { count: commentCount })}>{commentCount ?? "—"}</span></button>
    </div>
    <div id={id} hidden={!open}>{opened ? <CommunityComments targetType={targetType} targetUuid={targetUuid} loginHref={loginHref} onTotalChange={setCommentCount} /> : null}</div>
  </div>;
}
